import { useState, useEffect, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import dayjs from "dayjs";
import * as actions from "../../../../store/Expenses/actions";

export const useExpenseTable = () => {
  const dispatch = useDispatch();
  const isInitialMount = useRef(true);
  const prevFiltersRef = useRef({ from: null, to: null });
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [openAddEditExpenseModal, setOpenAddEditExpenseModal] = useState(false);
  const [mode, setMode] = useState("add");
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [fromDateFilterValue, setFromDateFilterValue] = useState(null);
  const [toDateFilterValue, setToDateFilterValue] = useState(null);
  const [fromDateHasError, setFromDateHasError] = useState(false);
  const [toDateHasError, setToDateHasError] = useState(false);
  const isLoading = useSelector((state) => state.loader);
  const expenses = useSelector((state) => state.expenses);
  const list = expenses?.list || [];
  const meta = expenses?.meta || {};

  // Load expenses on component mount and when the filter values and page change
  useEffect(() => {
    // Don't make API calls if there are validation errors
    if (fromDateHasError || toDateHasError) {
      return;
    }

    dispatch(
      actions.getExpensesRequest({
        items: 10,
        page: currentPage,
        after_date: fromDateFilterValue,
        before_date: toDateFilterValue,
      })
    );

    // Refresh statistics on initial mount or when filters change
    const filtersChanged =
      isInitialMount.current ||
      prevFiltersRef.current.from !== fromDateFilterValue ||
      prevFiltersRef.current.to !== toDateFilterValue;

    if (filtersChanged) {
      dispatch(
        actions.getMimarProfitRequest({
          after_date: fromDateFilterValue,
          before_date: toDateFilterValue,
        })
      );
      prevFiltersRef.current = {
        from: fromDateFilterValue,
        to: toDateFilterValue,
      };
      isInitialMount.current = false;
    }
  }, [
    dispatch,
    currentPage,
    fromDateFilterValue,
    toDateFilterValue,
    fromDateHasError,
    toDateHasError,
  ]);

  // Validation handlers that update both value and error state
  const handleFromDateChange = useCallback(
    (value) => {
      setFromDateFilterValue(value);
      // Validate "from date" - ensure it's not after "to date"
      if (
        value &&
        toDateFilterValue &&
        dayjs(value).isAfter(dayjs(toDateFilterValue))
      ) {
        setFromDateHasError(true);
      } else {
        setFromDateHasError(false);
      }
    },
    [toDateFilterValue]
  );

  const handleToDateChange = useCallback(
    (value) => {
      setToDateFilterValue(value);
      // Validate "to date" - ensure it's not before "from date"
      if (
        value &&
        fromDateFilterValue &&
        dayjs(value).isBefore(dayjs(fromDateFilterValue))
      ) {
        setToDateHasError(true);
      } else {
        setToDateHasError(false);
      }
    },
    [fromDateFilterValue]
  );

  const handleDeleteClick = (id) => {
    setSelectedExpense({ id });
    setOpenDeleteConfirm(true);
  };

  const handleCloseDeleteConfirmModal = () => {
    setOpenDeleteConfirm(false);
    setSelectedExpense(null);
  };

  const handleDeleteExpense = () => {
    if (selectedExpense?.id) {
      dispatch(
        actions.deleteExpenseRequest({
          id: selectedExpense.id,
          currentPage: currentPage,
          after_date: fromDateFilterValue,
          before_date: toDateFilterValue,
        })
      );
    }
    setOpenDeleteConfirm(false);
    setSelectedExpense(null);
  };

  const handleCloseAddEditExpenseModal = useCallback(() => {
    setOpenAddEditExpenseModal(false);
    setSelectedExpense(null);
  }, []);

  const handleOpenAddEditExpenseModal = (newMode, expense = null) => {
    setMode(newMode);
    setSelectedExpense(expense);
    setOpenAddEditExpenseModal(true);
  };

  const handleAddExpense = (expenseData) => {
    dispatch(
      actions.addExpenseRequest({
        expenseData,
        page: currentPage,
        after_date: fromDateFilterValue,
        before_date: toDateFilterValue,
      })
    );
  };

  const handleEditExpense = (expenseData) => {
    if (selectedExpense?.id) {
      dispatch(
        actions.editExpenseRequest({
          id: selectedExpense.id,
          expenseData,
          page: currentPage,
          after_date: fromDateFilterValue,
          before_date: toDateFilterValue,
        })
      );
    }
  };

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  return {
    // State
    openDeleteConfirm,
    openAddEditExpenseModal,
    mode,
    selectedExpense,
    currentPage,
    isLoading,
    list,
    meta,
    fromDateFilterValue,
    toDateFilterValue,
    fromDateHasError,
    toDateHasError,

    // Handlers
    handleDeleteClick,
    handleCloseDeleteConfirmModal,
    handleDeleteExpense,
    handleCloseAddEditExpenseModal,
    handleOpenAddEditExpenseModal,
    handleAddExpense,
    handleEditExpense,
    handlePageChange,
    handleFromDateChange,
    handleToDateChange,
  };
};
